import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SOCIAL_ERROR_MESSAGES, SOCIAL_PROVIDER_LABELS, SSO_ERROR_MESSAGES, SsoError, type SessionState, type SocialProvider } from '@chapsi/contracts';
import { ApiError, api } from '../../api/client.js';
import { keys } from '../../api/hooks.js';
import { canReturnFromProvider, isDesktopApp, onProviderReturn } from '../../lib/desktop.js';
import { autofillSupported, cancelPasskeyPrompt, markMagicLinkSignIn, passkeyErrorMessage, passkeysSupported, signInWithPasskey, wasCancelled } from '../../lib/passkeys.js';
import { providersToShow, socialNoticeFrom, socialNoticeFromDesktop, type SocialNotice } from '../../lib/social.js';
import { nextRoute } from '../../lib/two-factor.js';
import { AuthLayout } from './AuthLayout.js';
import { continueWithSso } from './Sso.js';

/**
 * A failed single sign-on comes back as `?sso_error=<code>` (docs/adr/0055), a failed Google or Microsoft sign-in as
 * `?social_error=<code>` (docs/adr/0081, with `#email=` when the address has no account yet). The messages are ours,
 * never the provider's. Read once, then the address is tidied so a reload does not show them again.
 */
function noticesFromUrl(): { sso: string | null; social: SocialNotice | null } {
  const search = window.location.search;
  const code = SsoError.safeParse(new URLSearchParams(search).get('sso_error'));
  const social = socialNoticeFrom(search, window.location.hash);
  const sso = code.success ? SSO_ERROR_MESSAGES[code.data] : null;
  if (sso || social) history.replaceState(null, '', window.location.pathname);
  return { sso, social };
}

/** The providers' own marks, as their brand guidelines ask for on sign-in buttons. Decorative: the label names them. */
function ProviderMark({ provider }: { provider: SocialProvider }) {
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  );
}

/** Passwordless sign-in (brief: Identity and access). The same screen signs people up. */
export function SignIn() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [initial] = useState(noticesFromUrl);
  const [email, setEmail] = useState(initial.social?.email ?? '');
  const [flowId, setFlowId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(initial.sso ?? (initial.social && initial.social.code !== 'no_account' ? initial.social.message : null));
  // Continue with Google or Microsoft (docs/adr/0081): which providers the server has, a calm note (an address with no
  // account yet), and on the desktop the secret for the code the shell hands back (memory only, single use).
  const [socialNote, setSocialNote] = useState<string | null>(initial.social?.code === 'no_account' ? initial.social.message : null);
  const [socialBusy, setSocialBusy] = useState<SocialProvider | null>(null);
  const [inBrowser, setInBrowser] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const desktopSecret = useRef<string | null>(null);
  const providerList = useQuery({ queryKey: ['social-providers'], queryFn: () => api('socialProviders'), staleTime: 5 * 60_000, retry: false });
  const providers = providersToShow(providerList.data?.providers, { desktop: isDesktopApp(), desktopCanReturn: canReturnFromProvider() });
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyNote, setPasskeyNote] = useState<string | null>(null);
  const supported = passkeysSupported();
  const mounted = useRef(true);

  const finish = useCallback(
    (state: SessionState) => {
      qc.setQueryData(keys.session, state);
      void navigate({ to: nextRoute(state) });
    },
    [navigate, qc],
  );

  // Conditional UI (docs/adr/0026-passkeys.md): the browser lists this site's passkeys in the email
  // field's suggestions; picking one signs in. Waits quietly; cancelled when the screen changes.
  const startAutofill = useCallback(async () => {
    if (!(await autofillSupported()) || !mounted.current) return;
    try {
      const state = await signInWithPasskey(true);
      if (mounted.current) finish(state);
    } catch (err) {
      if (mounted.current && !wasCancelled(err)) setPasskeyNote(passkeyErrorMessage(err));
    }
  }, [finish]);

  useEffect(() => {
    mounted.current = true;
    if (!flowId) void startAutofill();
    return () => {
      mounted.current = false;
      cancelPasskeyPrompt();
    };
  }, [flowId, startAutofill]);

  // The desktop shell brings the provider's answer back to this window (docs/adr/0081): trade the code with the secret
  // this page kept, as a browser, so the session cookie lands in the app's own profile.
  useEffect(
    () =>
      onProviderReturn((back) => {
        const read = socialNoticeFromDesktop(back);
        setInBrowser(false);
        if (read.kind === 'notice') {
          desktopSecret.current = null;
          if (read.code === 'no_account') {
            if (read.email) setEmail(read.email);
            setSocialNote(read.message);
            setError(null);
          } else {
            setError(read.message);
          }
          return;
        }
        const secret = desktopSecret.current;
        desktopSecret.current = null;
        if (!secret) {
          setError('That sign-in expired or was already used. Start again.');
          return;
        }
        setExchanging(true);
        api('exchangeSocial', { body: { code: read.code, secret } })
          .then((state) => { if (mounted.current) finish(state); })
          .catch((err: unknown) => { if (mounted.current) setError(err instanceof ApiError ? err.message : 'That sign-in did not finish. Try again.'); })
          .finally(() => { if (mounted.current) setExchanging(false); });
      }),
    [finish],
  );

  const continueWithProvider = async (provider: SocialProvider) => {
    setSocialBusy(provider);
    setError(null);
    setSocialNote(null);
    try {
      if (isDesktopApp()) {
        const r = await api('startSocial', { params: { provider }, body: { client: 'desktop' } });
        desktopSecret.current = r.secret ?? null;
        // The window shows only Chapsi: the shell opens the provider's page in the default browser.
        window.open(r.url, '_blank', 'noopener');
        setInBrowser(true);
        setSocialBusy(null);
        return;
      }
      const r = await api('startSocial', { params: { provider }, body: { client: 'web' } });
      window.location.assign(r.url);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Switched off since the screen loaded: say so in our words, and ask again which providers there are.
        setError(SOCIAL_ERROR_MESSAGES.unavailable);
        void providerList.refetch();
      } else {
        setError(err instanceof ApiError ? err.message : `We couldn't reach ${SOCIAL_PROVIDER_LABELS[provider]}. Try again, or use an emailed link.`);
      }
      setSocialBusy(null);
    }
  };

  const passkey = async () => {
    if (!supported) {
      setPasskeyNote('This browser does not support passkeys. Use the email link instead.');
      return;
    }
    setPasskeyBusy(true);
    setPasskeyNote(null);
    setError(null);
    try {
      finish(await signInWithPasskey(false));
    } catch (err) {
      if (!mounted.current) return;
      setPasskeyNote(passkeyErrorMessage(err));
      void startAutofill();
    } finally {
      if (mounted.current) setPasskeyBusy(false);
    }
  };

  const request = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Single sign-on first (docs/adr/0055): an address on a verified domain goes to its identity provider.
      if (await continueWithSso(email).catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'rate_limited') throw err;
        return false;
      })) {
        setRedirecting(true);
        return;
      }
      const r = await api('requestMagicLink', { body: { email } });
      setFlowId(r.flowId);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'sso_required' && (await continueWithSso(email).catch(() => false))) {
        setRedirecting(true);
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Could not send the email. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flowId) return;
    setBusy(true);
    setError(null);
    try {
      const state = await api('verifyMagicLink', { body: { flowId, code } });
      markMagicLinkSignIn();
      finish(state);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (redirecting) {
    return (
      <AuthLayout>
        <h1 role="status">Opening your identity provider…</h1>
        <p className="auth-sso-note">Your organisation signs in with single sign-on. If nothing happens, <button type="button" className="link" onClick={() => setRedirecting(false)}>go back</button>.</p>
      </AuthLayout>
    );
  }

  if (flowId) {
    return (
      <AuthLayout>
        <h1>Check your email</h1>
        <p>
          We sent a sign-in link to <b style={{ color: 'var(--c-text)' }}>{email}</b>. Open it in this browser, or type the 6-digit code here.
        </p>
        <form onSubmit={verifyCode} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-12)' }}>
          <label className="sr-only" htmlFor="code">6-digit code</label>
          <input id="code" className="input code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
          {error && <div className="error" role="alert">{error}</div>}
          <button className="btn btn-primary" disabled={code.length !== 6 || busy}>Sign in</button>
        </form>
        <button className="link" onClick={() => { setFlowId(null); setCode(''); }}>Use a different email</button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1>Sign in to Chapsi</h1>
      <p>Enter your work email. We will send you a link and a code; no password needed.</p>
      <form onSubmit={request} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-12)' }}>
        <div className="field">
          <label htmlFor="email">Work email</label>
          <input id="email" className="input" type="email" autoComplete="username webauthn" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus placeholder="you@company.com.au" />
        </div>
        {socialNote && <p className="auth-note" role="status" data-testid="social-note">{socialNote}</p>}
        {error && <div className="error" role="alert">{error}</div>}
        <button className="btn btn-primary" disabled={!email || busy}>Email me a sign-in link</button>
        <p className="auth-sso-note">Does your company use single sign-on? Enter your work email and we'll take you there.</p>
      </form>
      <div className="auth-or" aria-hidden="true"><span>or</span></div>
      <button type="button" className="btn auth-passkey" onClick={() => void passkey()} disabled={passkeyBusy} aria-describedby={passkeyNote ? 'passkey-note' : undefined}>
        <KeyRound aria-hidden="true" /> {passkeyBusy ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
      </button>
      {passkeyNote && <p className="auth-note" id="passkey-note" role="status">{passkeyNote}</p>}
      {providers.length > 0 && (
        <div className="auth-providers" data-testid="social-sign-in">
          {providers.map((p) => (
            <button key={p} type="button" className="btn auth-passkey auth-provider" onClick={() => void continueWithProvider(p)} disabled={socialBusy !== null || exchanging}>
              <ProviderMark provider={p} /> {socialBusy === p ? `Opening ${SOCIAL_PROVIDER_LABELS[p]}…` : `Continue with ${SOCIAL_PROVIDER_LABELS[p]}`}
            </button>
          ))}
          {inBrowser && <p className="auth-note" role="status">Finish signing in in your browser. Chapsi comes back here when you're done.</p>}
          {exchanging && <p className="auth-note" role="status">Signing you in…</p>}
        </div>
      )}
      <p style={{ fontSize: 'var(--fs-sm)' }}>New to Chapsi? The same link creates your account.</p>
    </AuthLayout>
  );
}
